import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

import { getUrl } from "../../helpers/url";
import { fetchStatus } from "./preferenceSlice";

const initialState = {
  // sync rules
  stripeCustomerNumberPreference: {
    value: "msbc",
  },
  isContactSyncRulesSaved: false,
  isContactDefaultMappingsSaved: false,
  stripeFilterPreference: undefined,
  msbcFilterPreference: undefined,
  stripeCustomerPreference: undefined,
  msbcCustomerPreference: {
    name: "Customer Number",
    value: "customerNumber",
  },
  stripeCustomerNumberFields: [
    {
      title: "Let Business Central create customer number",
      value: "msbc",
    },
    {
      title: "Take customer number from Stripe",
      value: "stripe",
    },
  ],
  msbcCustomerDropdownFields: [
    {
      CRF: "customerNumber",
      HRF: "Customer Number",
    },
  ],
  stripeContactMetadataPreference: "",
  // default mappings
  paymentTermsCodePreference: undefined,
  currencyCodePreference: undefined,
  customerPostingGroupPreference: undefined,
  vatBusinessPostingGroupPreference: undefined,
  generalBusinessPostingGroupPreference: undefined,
  defaultMappingFields: {
    loading: false,
    defaultMappings: {},
    error: "",
    currentRequestId: undefined,
  },
};

/**
 * This function is responsible for fetching default mappings fields
 */
export const getDefaultMappingFields = createAsyncThunk(
  "contact/getDefaultMappingFields",
  async (account) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_GET_DEFAULT_MAPPINGS")}?account=${account}`
      );
      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      toast.error(errorMessage);
    }
  }
);

const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    /**
     * Reducer updates the state of the stripeCustomerNumberPreference
     */
    onChangeStripeCustomerNumberPreference(state, { payload }) {
      state.stripeCustomerNumberPreference = {
        value: payload.selectedOption,
      };
      if (state.stripeCustomerNumberPreference.value === "msbc") {
        state.stripeCustomerPreference = {
          name: "Do Not add it anywhere",
          value: "doNotAdd",
        };
      } else {
        state.stripeCustomerPreference = {
          name: "Customer Number",
          value: "id",
        };
      }
      state.isContactSyncRulesSaved = false;
    },
    /**
     * Reducer updates the state of field dropdowns
     */
    setSelectedFieldsDropdown(state, { payload }) {
      if (payload.selectingFor === "stripeSearchParameter") {
        state.stripeFilterPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
        state.isContactSyncRulesSaved = false;
      } else if (payload.selectingFor === "msbcSearchParameter") {
        state.msbcFilterPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
        state.isContactSyncRulesSaved = false;
      } else if (payload.selectingFor === "stripeCustomerField") {
        state.stripeCustomerPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
        state.isContactSyncRulesSaved = false;
      } else if (payload.selectingFor === "msbcCustomerField") {
        state.msbcCustomerPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
        state.isContactSyncRulesSaved = false;
      } else if (payload.selectingFor === "paymentTermsCode") {
        state.paymentTermsCodePreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "currencyCode") {
        state.currencyCodePreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "customerPostingGroup") {
        state.customerPostingGroupPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "vatBusinessPostingGroup") {
        state.vatBusinessPostingGroupPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "generalBusinessPostingGroup") {
        state.generalBusinessPostingGroupPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      }
    },
    /**
     * Reducer updates the state of radio buttons that are used in contact default mappings
     */
    onChangeSelectedDefaultValues(state, { payload }) {
      state.selectedDefaultPreference = payload.selectedValue;
    },
    /**
     * Reducer updates the state of isContactSyncRulesSaved when user clicked on save button
     */
    setIsContactSyncRulesSaved(state, { payload }) {
      state.isContactSyncRulesSaved = true;
    },
    /**
     * Reducer updates the state of isContactDefaultMappingsSaved when user clicked on save button
     */
    setIsContactDefaultMappingsSaved(state, { payload }) {
      state.isContactDefaultMappingsSaved = true;
    },
    /**
     * Reducer updates the state of stripeContactMetadataPreference when user enters the data in text box
     */
    onChangeStripeContactMetadataPreference(state, { payload }) {
      state.stripeContactMetadataPreference = payload.enteredInput;
      state.isContactSyncRulesSaved = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDefaultMappingFields.pending, (state, actions) => {
      if (!state.defaultMappingFields.loading) {
        state.defaultMappingFields.loading = true;
        state.defaultMappingFields.currentRequestId = actions.meta.requestId;
      }
    });
    builder.addCase(getDefaultMappingFields.fulfilled, (state, actions) => {
      if (
        state.defaultMappingFields.loading &&
        state.defaultMappingFields.currentRequestId === actions.meta.requestId
      ) {
        state.defaultMappingFields.loading = false;
        state.defaultMappingFields.defaultMappings = actions.payload;
        state.defaultMappingFields.error = "";
        state.defaultMappingFields.currentRequestId = undefined;
      }
    });
    builder.addCase(getDefaultMappingFields.rejected, (state, actions) => {
      if (
        state.defaultMappingFields.loading &&
        state.defaultMappingFields.currentRequestId === actions.meta.requestId
      ) {
        state.defaultMappingFields.loading = false;
        state.defaultMappingFields.defaultMappings = {};
        state.defaultMappingFields.error = actions.error.message ?? "";
        state.defaultMappingFields.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchStatus.fulfilled, (state, actions) => {
      const { preference } = actions.payload;
      // updating the preferences
      if (preference !== undefined && Object.keys(preference).length > 0) {
        const { customer } = preference;
        if (customer && Object.keys(customer).length > 0) {
          // Update contact sync rules preferences
          if (customer?.filterOptions) {
            if (
              customer.filterOptions.filterStripeProperty.name === "Metadata"
            ) {
              state.stripeFilterPreference = { name: "Metadata", value: "key" };
              state.stripeContactMetadataPreference =
                customer.filterOptions.filterStripeProperty.value;
            } else {
              state.stripeFilterPreference = customer.filterOptions
                ?.filterStripeProperty?.value
                ? customer.filterOptions.filterStripeProperty
                : undefined;
            }
            state.msbcFilterPreference = customer.filterOptions
              ?.filterMsbcProperty?.value
              ? customer.filterOptions.filterMsbcProperty
              : undefined;
          }

          if (customer?.stripeCustomerNumber) {
            state.stripeCustomerNumberPreference = {
              value:
                customer.stripeCustomerNumber.direction === ""
                  ? "msbc"
                  : customer.stripeCustomerNumber.direction,
            };

            if (customer?.stripeCustomerNumber?.mapping) {
              state.stripeCustomerPreference = customer.stripeCustomerNumber
                ?.mapping?.stripeProperty?.value
                ? customer.stripeCustomerNumber.mapping.stripeProperty
                : undefined;
            }
          }
          // Update contact default mappings preferences
          if (customer?.stripeFields) {
            const {
              paymentTerms,
              currency,
              customerPostingGroup,
              vatBusinessPostingGroup,
              generalBusinessPostingGroup,
            } = customer.stripeFields;

            state.paymentTermsCodePreference = paymentTerms?.value
              ? paymentTerms
              : undefined;
            state.currencyCodePreference = currency?.value
              ? currency
              : undefined;
            state.customerPostingGroupPreference = customerPostingGroup?.value
              ? customerPostingGroup
              : undefined;
            state.vatBusinessPostingGroupPreference =
              vatBusinessPostingGroup?.value
                ? vatBusinessPostingGroup
                : undefined;
            state.generalBusinessPostingGroupPreference =
              generalBusinessPostingGroup?.value
                ? generalBusinessPostingGroup
                : undefined;
          }
        }
      }
      // Check all the fields in the contact sync rules are mapped
      if (
        state.stripeFilterPreference &&
        state.msbcFilterPreference &&
        state.stripeCustomerPreference &&
        state.stripeCustomerNumberPreference.value
      ) {
        state.isContactSyncRulesSaved = true;
      }
      // Check all the fields in the contact default mappings are mapped
      if (
        state.paymentTermsCodePreference &&
        state.currencyCodePreference &&
        state.customerPostingGroupPreference &&
        state.vatBusinessPostingGroupPreference &&
        state.generalBusinessPostingGroupPreference
      ) {
        state.isContactDefaultMappingsSaved = true;
      }
    });
  },
});

export const {
  onChangeStripeCustomerNumberPreference,
  setSelectedFieldsDropdown,
  onChangeSelectedDefaultValues,
  setIsContactSyncRulesSaved,
  setIsContactDefaultMappingsSaved,
  onChangeStripeContactMetadataPreference,
  onChangeContactDefaultMappingsTextBoxes,
} = contactSlice.actions;

export default contactSlice.reducer;
